import { Callout, DirectionalHint, ICalloutProps, IDropdownOption, Stack } from '@fluentui/react';
import { Button, buttonStylesGhost, buttonStylesPrimary, DatePicker, Dropdown } from '@h2oai/ui-kit';
import { useCallback, useEffect, useState } from 'react';

import { Granularity } from '../../../telemetry/gen/ai/h2o/cloud/telemetry_read_api/v1/aiunits_pb';
import { getAllGranularityOptionsWithDisabled, getValidGranularityOptions } from '../constants/granularity';
import { DateRangeType } from '../types';
import { getDaysAgo } from './TimeSeries';

const quickRangeButtonStyle = {
  root: {
    minWidth: 'auto',
    height: 20,
    minHeight: 20,
    padding: '0px 8px',
    fontSize: 12,
  },
};

interface DateRangeCalloutProps {
  isVisible: boolean;
  targetElement: HTMLElement | null;
  startTime: Date;
  endTime: Date;
  granularity: Granularity;
  dateRangeType: DateRangeType;
  onApply: (startTime: Date, endTime: Date, granularity: Granularity, dateRangeType: DateRangeType) => void;
  onCancel: () => void;
}

export function DateRangeCallout({
  isVisible,
  targetElement,
  startTime,
  endTime,
  granularity,
  dateRangeType,
  onApply,
  onCancel,
}: DateRangeCalloutProps) {
  // Local state for pending changes
  const [pendingStartTime, setPendingStartTime] = useState<Date>(startTime);
  const [pendingEndTime, setPendingEndTime] = useState<Date>(endTime);
  const [pendingGranularity, setPendingGranularity] = useState<Granularity>(granularity);
  const [pendingDateRangeType, setPendingDateRangeType] = useState<DateRangeType>(dateRangeType);

  // Update local state when props change and callout becomes visible
  // Fixed: Only sync when callout opens to prevent unnecessary updates
  useEffect(() => {
    if (isVisible) {
      setPendingStartTime(startTime);
      setPendingEndTime(endTime);
      setPendingGranularity(granularity);
      setPendingDateRangeType(dateRangeType);
    }
  }, [isVisible, startTime, endTime, granularity, dateRangeType]);

  const onChangeStartDate = useCallback(
    (newStartTime: Date | null | undefined) => {
      if (newStartTime) {
        setPendingStartTime(newStartTime);
        setPendingDateRangeType('custom');
        const validGranularityOptions = getValidGranularityOptions(newStartTime, pendingEndTime);
        if (!validGranularityOptions.find((option) => option.key === pendingGranularity)) {
          setPendingGranularity(validGranularityOptions[0].key);
        }
      }
    },
    [pendingEndTime, pendingGranularity]
  );

  const onChangeEndDate = useCallback(
    (newEndTime: Date | null | undefined) => {
      if (newEndTime) {
        setPendingEndTime(newEndTime);
        setPendingDateRangeType('custom');
        const validGranularityOptions = getValidGranularityOptions(pendingStartTime, newEndTime);
        if (!validGranularityOptions.find((option) => option.key === pendingGranularity)) {
          setPendingGranularity(validGranularityOptions[0].key);
        }
      }
    },
    [pendingStartTime, pendingGranularity]
  );

  const onChangeGranularity = useCallback((_event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
    if (option) {
      setPendingGranularity(option.key as Granularity);
    }
  }, []);

  // Fixed: Use functional state update to prevent race conditions in rapid clicks
  const onSelectQuickRange = useCallback(
    (rangeType: string) => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      let newStartDate = new Date(today);
      const newEndDate = new Date(today);
      newEndDate.setHours(23, 59, 59, 999); // Match timeRangePresets format

      switch (rangeType) {
        case '1day':
          newStartDate.setDate(today.getDate() - 1);
          break;
        case '7days':
          newStartDate.setDate(today.getDate() - 7);
          break;
        case 'mtd':
          newStartDate = new Date(today.getFullYear(), today.getMonth(), 1);
          break;
        case '1month':
          newStartDate.setDate(today.getDate() - 30);
          break;
        case '3months':
          newStartDate.setDate(today.getDate() - 90);
          break;
        case '6months':
          newStartDate.setDate(today.getDate() - 180);
          break;
        case '1year':
          newStartDate.setDate(today.getDate() - 365);
          break;
        case '3years':
          newStartDate.setDate(today.getDate() - 1095);
          break;
        case 'ytd':
          newStartDate = new Date(today.getFullYear(), 0, 1);
          break;
        default:
          return;
      }

      setPendingStartTime(newStartDate);
      setPendingEndTime(newEndDate);
      setPendingDateRangeType(rangeType as DateRangeType);

      // Validate and update granularity if needed using functional update
      setPendingGranularity((currentGranularity) => {
        const validGranularityOptions = getValidGranularityOptions(newStartDate, newEndDate);
        if (!validGranularityOptions.find((option) => option.key === currentGranularity)) {
          return validGranularityOptions[0].key;
        }
        return currentGranularity;
      });
    },
    [] // No dependencies - stable callback reference
  );

  const handleApply = useCallback(() => {
    onApply(pendingStartTime, pendingEndTime, pendingGranularity, pendingDateRangeType);
  }, [pendingStartTime, pendingEndTime, pendingGranularity, pendingDateRangeType, onApply]);

  const handleCancel = useCallback(() => {
    // Reset to original values
    setPendingStartTime(startTime);
    setPendingEndTime(endTime);
    setPendingGranularity(granularity);
    setPendingDateRangeType(dateRangeType);
    onCancel();
  }, [startTime, endTime, granularity, dateRangeType, onCancel]);

  if (!isVisible || !targetElement) {
    return null;
  }

  const calloutProps: ICalloutProps = {
    target: targetElement,
    directionalHint: DirectionalHint.leftTopEdge,
    isBeakVisible: false,
    gapSpace: 8,
    onDismiss: handleCancel,
    setInitialFocus: true,
  };

  return (
    <Callout {...calloutProps}>
      <div
        style={{
          width: 320,
          padding: 16,
          background: 'white',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <DatePicker
            ariaLabel="Change start date"
            styles={{ root: { width: '100%' } }}
            label="Start Date"
            onSelectDate={onChangeStartDate}
            value={pendingStartTime}
            maxDate={getDaysAgo(1)}
          />
          <DatePicker
            ariaLabel="Change end date"
            data-test="platform-usage-change-end-date"
            styles={{ root: { width: '100%' } }}
            label="End Date"
            onSelectDate={onChangeEndDate}
            value={pendingEndTime}
            maxDate={getDaysAgo(0)}
          />

          {/* Quick Date Range Selection */}
          <div>
            <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 8 }}>Quick Range</div>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              <Button text="1 Day" onClick={() => onSelectQuickRange('1day')} styles={quickRangeButtonStyle} />
              <Button text="7 Days" onClick={() => onSelectQuickRange('7days')} styles={quickRangeButtonStyle} />
              <Button text="MTD" onClick={() => onSelectQuickRange('mtd')} styles={quickRangeButtonStyle} />
              <Button text="1 Month" onClick={() => onSelectQuickRange('1month')} styles={quickRangeButtonStyle} />
              <Button text="3 Months" onClick={() => onSelectQuickRange('3months')} styles={quickRangeButtonStyle} />
              <Button
                text="6 Months"
                onClick={() => onSelectQuickRange('6months')}
                styles={quickRangeButtonStyle}
                disabled={true}
              />
              <Button
                text="1 Year"
                onClick={() => onSelectQuickRange('1year')}
                styles={quickRangeButtonStyle}
                disabled={true}
              />
              <Button
                text="3 Years"
                onClick={() => onSelectQuickRange('3years')}
                styles={quickRangeButtonStyle}
                disabled={true}
              />
              <Button
                text="YTD"
                onClick={() => onSelectQuickRange('ytd')}
                styles={quickRangeButtonStyle}
                disabled={true}
              />
            </div>
          </div>

          <Dropdown
            ariaLabel="Change granularity"
            styles={{ root: { width: '100%' } }}
            label="Granularity"
            options={getAllGranularityOptionsWithDisabled(pendingStartTime, pendingEndTime)}
            selectedKey={pendingGranularity}
            onChange={onChangeGranularity}
          />

          {/* Action Buttons */}
          <Stack horizontal horizontalAlign="end" tokens={{ childrenGap: 8 }}>
            <Button styles={buttonStylesGhost} text="Cancel" onClick={handleCancel} />
            <Button styles={buttonStylesPrimary} text="Apply" onClick={handleApply} />
          </Stack>
        </div>
      </div>
    </Callout>
  );
}
